// FILE: src/components/PlatformBadgeRow.js
//
// PlatformBadgeRow — the dedicated ROW of Platform/Feature badges in the hero's
// right column. It shows ONE badge at a time and gently rotates through the set
// on the cadence in BADGE_ROTATION_CONFIG (20–30s band), with manual prev/next
// chevrons and clickable progress dots so a learner can jump straight to any
// badge.
//
// Data source (admin-managed, migration 029):
//   • On mount it loads the active platform badges from the database
//     (badgeAdminService.getActivePlatformBadges), ordered by sort_order.
//   • If that read fails (Supabase unreachable) it falls back to the static
//     set in src/config/badges.js so the row still renders offline.
//   • While loading it renders nothing (avoids a flash), and renders nothing
//     when there are no active badges.
//
// Behaviour:
//   • rotation interval + visibleCount + pause-on-hover come from config
//   • prefers-reduced-motion → no auto-rotation; manual controls still work
//   • rotation also pauses while the tab is hidden
//   • after a manual advance, auto-rotation pauses ~10s so the chosen badge
//     stays on screen long enough to read before it swaps

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BADGE_ROTATION_CONFIG } from '../../config/badges';
import { getPlatformBadges, getVisiblePlatformBadges } from '../../services/badgeService';
import { getActivePlatformBadges } from '../../services/badgeAdminService';
import BadgeCallout from './BadgeCallout';
import styles from './PlatformBadgeRow.module.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Auto-rotation pauses for this long after a manual prev/next/dot click.
const MANUAL_PAUSE_MS = 10000;

/**
 * @param {string}  [eyebrow='Inside PM Pixel']  small label before the badge.
 * @param {string}  [certId='*']                 cert scope for the static fallback.
 * @param {number}  [intervalMs]                 override rotation cadence (tests).
 */
export default function PlatformBadgeRow({
  eyebrow = 'Inside PM Pixel',
  certId = '*',
  intervalMs = BADGE_ROTATION_CONFIG.intervalMs,
}) {
  const [badges, setBadges] = useState(undefined);
  useEffect(() => {
    let alive = true;
    getActivePlatformBadges().then((rows) => {
      if (!alive) return;
      setBadges(rows === null ? getPlatformBadges(certId) : rows);
    });
    return () => {
      alive = false;
    };
  }, [certId]);

  const [tick, setTick] = useState(0);
  const pausedRef = useRef(false);
  const manualPauseUntilRef = useRef(0);
  const reduced = prefersReducedMotion();
  const list = Array.isArray(badges) ? badges : [];

  const pauseAfterManual = useCallback(() => {
    manualPauseUntilRef.current = Date.now() + MANUAL_PAUSE_MS;
  }, []);
  const goTo = useCallback(
    (i) => {
      setTick(i);
      pauseAfterManual();
    },
    [pauseAfterManual]
  );
  const next = useCallback(() => {
    setTick((t) => t + 1);
    pauseAfterManual();
  }, [pauseAfterManual]);
  const prev = useCallback(() => {
    setTick((t) => t - 1);
    pauseAfterManual();
  }, [pauseAfterManual]);

  useEffect(() => {
    if (reduced || list.length <= BADGE_ROTATION_CONFIG.visibleCount) return undefined;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      if (Date.now() < manualPauseUntilRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setTick((t) => t + 1);
    }, intervalMs);
    return () => clearInterval(id);
  }, [reduced, list.length, intervalMs]);

  if (badges === undefined) return null;
  if (list.length === 0) return null;

  const visible = getVisiblePlatformBadges(list, tick);
  const activeIndex = list.length ? ((tick % list.length) + list.length) % list.length : 0;
  const showControls = list.length > 1;

  return (
    <div
      className={styles.row}
      role="region"
      aria-label="PM Pixel feature highlights"
      data-testid="platform-badge-row"
      onMouseEnter={() => {
        if (BADGE_ROTATION_CONFIG.pauseOnHover) pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <span className={styles.eyebrow}>{eyebrow}</span>

      {showControls && (
        <button
          type="button"
          className={styles.nav}
          onClick={prev}
          aria-label="Previous feature"
          data-testid="platform-badge-prev"
        >
          ‹
        </button>
      )}

      <span className={styles.stage}>
        {visible.map((badge) => (
          <BadgeCallout key={badge.id} badge={badge} animate="fade" size="lg" />
        ))}
      </span>

      {showControls && (
        <button
          type="button"
          className={styles.nav}
          onClick={next}
          aria-label="Next feature"
          data-testid="platform-badge-next"
        >
          ›
        </button>
      )}

      {showControls && (
        <span className={styles.dots} data-testid="platform-badge-dots">
          {list.map((b, i) => (
            <button
              key={b.id}
              type="button"
              data-testid="platform-badge-dot"
              className={i === activeIndex ? `${styles.dot} ${styles.dotOn}` : styles.dot}
              aria-label={`Show ${b.label}`}
              aria-current={i === activeIndex ? 'true' : undefined}
              onClick={() => goTo(i)}
            />
          ))}
        </span>
      )}
    </div>
  );
}
